import React, { useEffect, useMemo, useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import useTranslation from 'Hooks/useTranslation';
import usePageSize from 'Hooks/usePageSize';
import SideBarContainer from 'Containers/SideBarContainer';
import { Pagination, StatusIndicator, SearchBox, FilterSelect } from 'Components/ui';
import { LoadingState, PageBody, Subtitle, Title, Toolbar } from 'Components/ui/layout';
import { DataTable } from 'Components/ui';
import useDepartments from './Hooks/useDepartment';
import useRoute from './Hooks/useRoute';

const DEFAULT_PAGE_SIZE = 15;

/** El estatus llega como booleano; el filtro necesita valores con nombre. */
const STATUS = { active: 'active', inactive: 'inactive' };

/**
 * Selección de departamento para definir sus cursos.
 *
 * La pantalla no define nada: solo lleva al departamento elegido. Por eso no
 * hay casillas ni guardado, y el listado completo se busca y pagina en el
 * navegador.
 */
export default function DefinitionSelectDepartment() {
  const location = useLocation();
  const { t: translate } = useTranslation();
  const { t2 } = useTranslation('Department');

  const [isLoading, setIsLoading] = useState(true);
  const { departments } = useDepartments(setIsLoading);
  const { handlePage } = useRoute();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return departments.filter(department => {
      const matchesSearch = needle === '' || department.name?.toLowerCase().includes(needle);
      const matchesStatus =
        statusFilter.length === 0 || statusFilter.includes(department.active ? STATUS.active : STATUS.inactive);
      return matchesSearch && matchesStatus;
    });
  }, [departments, search, statusFilter]);

  const [pageSize, setPageSizeState] = usePageSize('definitionSelectDepartment', DEFAULT_PAGE_SIZE);

  // Cambiar el tamaño vuelve a la primera página: quien estaba en la 7 con 10
  // por página se saldría del rango al pedir 100, y la tabla saldría vacía.
  const setPageSize = value => {
    setPageSizeState(value);
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const visible = filtered.slice((page - 1) * pageSize, page * pageSize);

  const columns = [
    { key: 'name', label: t2('Name'), width: '2.4fr', strong: true },
    { key: 'numberOfEmployees', label: translate('Generic.NumberOfEmployees'), width: '1fr' },
    { key: 'numberOfCourses', label: translate('Generic.NumberOfCourses'), width: '1fr' },
    {
      key: 'active',
      label: t2('Status'),
      width: '1.1fr',
      render: row => (
        <StatusIndicator
          status={row.active ? 'active' : 'inactive'}
          label={row.active ? translate('Generic.Active') : translate('Generic.Inactive')}
        />
      ),
    },
  ];

  if (location?.state?.isComingFrom !== '/definicion') {
    return <Redirect to="/definicion" />;
  }

  return (
    <SideBarContainer>
      <PageBody>
        <div>
          <Title>{t2('Title')}</Title>
          <Subtitle>{t2('SubTitle')}</Subtitle>
        </div>

        <Toolbar>
          <SearchBox value={search} placeholder={t2('Search')} onChange={setSearch} />
          <FilterSelect
            value={statusFilter}
            options={[
              { value: STATUS.active, label: translate('Generic.Active') },
              { value: STATUS.inactive, label: translate('Generic.Inactive') },
            ]}
            placeholder={t2('Status')}
            onChange={setStatusFilter}
          />
        </Toolbar>

        {isLoading ? (
          <LoadingState>{t2('Loading')}</LoadingState>
        ) : (
          <>
            <DataTable
              rows={visible}
              columns={columns}
              labels={{ empty: t2('Empty'), viewDetails: t2('ViewDetails') }}
              onViewDetails={handlePage}
            />
            {filtered.length > 0 ? (
              <Pagination
                page={page}
                pageSize={pageSize}
                totalPages={totalPages}
                totalItems={filtered.length}
                onChange={setPage}
                onPageSizeChange={setPageSize}
                labels={{
                  showing: t2('PaginationShowing'),
                  of: t2('PaginationOf'),
                  page: t2('PaginationPage'),
                  prev: t2('PaginationPrev'),
                  next: t2('PaginationNext'),
                  first: t2('PaginationFirst'),
                  last: t2('PaginationLast'),
                  perPage: t2('PaginationPerPage'),
                }}
              />
            ) : null}
          </>
        )}
      </PageBody>
    </SideBarContainer>
  );
}
